import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// MS-recommended redirect bridge pattern:
// This route lives OUTSIDE AuthProvider (see App.js) and makes NO auth API
// calls beyond the single oauth/complete POST. After cookies are set, it
// performs a full page navigation so the main app mounts fresh with the
// new cookie already in place. This eliminates the mount race where
// AuthProvider.loadProfile() fired alongside the callback and hit a 401
// with the stale cookie, forcing a second sign-in.

const OAuthCallback = () => {
  const location = useLocation();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(location.search);

      // Check if this is an OAuth success callback
      if (urlParams.get('oauth_success') === 'true') {
        try {
          // Retrieve tokens from session via secure endpoint.
          // This is the ONLY auth-related call this page makes.
          const response = await fetch('/api/auth/oauth/complete', {
            method: 'POST',
            credentials: 'include'  // Include session cookie
          });

          if (response.ok) {
            const data = await response.json();

            // Handle company selection requirement (user belongs to multiple companies)
            if (data.status === 'company_select_required' || data.company_select_required === true) {
              if (!data.company_select_token || !data.companies) {
                console.error('Company selection required but no data received');
                window.location.href = '/login?error=company_select_failed';
                return;
              }

              // Store company selection data for LoginPage to use.
              // Using sessionStorage for security - cleared when tab closes, isolated per tab
              sessionStorage.setItem('oauth_company_select', JSON.stringify({
                token: data.company_select_token,
                companies: data.companies
              }));
              // Full page reload — LoginPage mounts fresh and reads sessionStorage
              window.location.href = '/login?company_select=true';
              return;
            }

            // SSO MFA step-up: user must verify MFA after OAuth login
            if (data.status === 'mfa_required' && data.mfa_required === true) {
              sessionStorage.setItem('mfa_token', data.mfa_token);
              if (data.mfa_methods) {
                sessionStorage.setItem('mfa_methods', JSON.stringify(data.mfa_methods));
              }
              window.location.href = '/mfa-verify';
              return;
            }

            // SSO MFA enrollment required
            if (data.status === 'mfa_enrollment_required') {
              sessionStorage.setItem('mfa_enrollment_token', data.mfa_token);
              if (data.message) sessionStorage.setItem('mfa_enrollment_message', data.message);
              if (data.mfa_policy) sessionStorage.setItem('mfa_enrollment_policy', data.mfa_policy);
              window.location.href = '/mfa-setup';
              return;
            }

            if (data.status === 'pending_approval') {
              // User has no company assigned — redirect to pending approval page
              sessionStorage.setItem('pending_approval', JSON.stringify({
                user_email: data.user_email,
                user_name: data.user_name,
                access_token: data.access_token,
              }));
              window.location.href = '/pending';
              return;
            }

            if (data.status === 'success') {
              // SP-initiated SSO continue — already a full navigation
              if (data.oauth_redirect) {
                window.location.href = data.oauth_redirect;
              } else {
                // Full page reload — main app mounts fresh with the new cookie.
                // AuthProvider.loadProfile() will succeed on the first try.
                // scheduleProactiveRefresh() is called by AuthProvider after
                // successful profile fetch — NOT here.
                window.location.href = '/';
              }
              return;
            } else {
              console.error('OAuth complete failed:', data);
              window.location.href = '/login?error=oauth_failed';
              return;
            }
          } else {
            console.error('OAuth complete failed:', response.status);
            window.location.href = '/login?error=oauth_failed';
            return;
          }
        } catch (err) {
          console.error('OAuth completion error:', err);
          window.location.href = '/login?error=oauth_failed';
          return;
        }
      } else if (urlParams.get('error')) {
        // OAuth error - redirect to login with error message
        const errorMsg = urlParams.get('message') || 'oauth_failed';
        window.location.href = `/login?error=${errorMsg}`;
        return;
      }
    };

    handleOAuthCallback();
  }, [location]);

  // Show loading state while processing
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
